import {
  Box,
  Checkbox,
  Divider,
  FormControlSizes,
  HStack,
  ICON_SIZES,
  Icon,
  IconName,
  IndicatorBadge,
  NumberInput,
  ProductTypeEnum,
  SideEnum,
  Text,
  Tooltip,
  VStack,
  formattedUTCDate,
  isFuture,
  isOption,
  isSpot,
  useDynamicCallback,
  useMarketsContext,
  useSecurity,
  type MultilegMarketDetails,
} from '@talos/kyoko';
import { useRef, type RefObject } from 'react';
import styled from 'styled-components';
import { OPTION_DATE_FORMAT } from '../OMS/NewOrder/models';
import { MultilegMarketAccountSelectorButton } from './MultilegMarketSelector';
import { Ratio, Section } from './styles';
import { getInfoRow } from './utils';

interface LegSummaryProps {
  legIndex: number | undefined;
  isExpanded: boolean;
  onToggle: () => void;
  symbol: string;

  isInitiating?: boolean;
  haircutRatio?: string;
  prettyLegName?: string;
  marketDetails?: MultilegMarketDetails[];
  legMarketNames: string[];
  showLegMarketChanges: boolean;
  readOnly: boolean;
  touched: boolean;
  supportsAllMarkets: boolean;
  onOpenMarketSelector: (args: {
    legIndex: number;
    buttonAnchorRef: RefObject<HTMLButtonElement>;
    panelAnchorRef: RefObject<HTMLDivElement>;
  }) => void;
  onInitiatingLegChange: (args: { legIndex: number; value: boolean }) => void;
  onHaircutRatioChange: (args: { legIndex: number; value: string }) => void;
  enableHaircutRatio?: boolean;
  side: SideEnum;
}

export const DefaultLegSummary = ({
  legIndex,
  isExpanded,
  prettyLegName,
  onToggle,
  symbol,
  isInitiating,
  haircutRatio,
  marketDetails,
  legMarketNames,
  readOnly,
  touched,
  showLegMarketChanges,
  supportsAllMarkets,
  onOpenMarketSelector,
  onInitiatingLegChange,
  onHaircutRatioChange,
  enableHaircutRatio,
  side,
}: LegSummaryProps) => {
  const security = useSecurity(symbol);
  const { marketsByName } = useMarketsContext();

  const ratio = side === SideEnum.Buy ? +1 : -1;

  const exchange = marketsByName.get(security?.Markets?.[0] || '');
  const expiration = security?.Expiration
    ? formattedUTCDate(new Date(security?.Expiration), OPTION_DATE_FORMAT).toUpperCase()
    : '';

  const handleInitiatingChange = useDynamicCallback(() => {
    onInitiatingLegChange({ legIndex: legIndex ?? -1, value: !isInitiating });
  });

  const handleHaircutRatioChange = useDynamicCallback((value: string) => {
    onHaircutRatioChange({ legIndex: legIndex ?? -1, value });
  });

  const sectionRef = useRef<HTMLDivElement>(null);
  const editMarketsButtonRef = useRef<HTMLButtonElement>(null);
  const marketSelectorLegIndex = legIndex ?? -1;

  const handleOpenMarketSelector = useDynamicCallback(() => {
    onOpenMarketSelector({
      legIndex: marketSelectorLegIndex,
      buttonAnchorRef: editMarketsButtonRef,
      panelAnchorRef: sectionRef,
    });
  });

  const handleToggle = useDynamicCallback(() => {
    onToggle();
    setTimeout(() => {
      sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }, 200);
  });

  if (!security) {
    return <></>;
  }

  const supportsAsset = !isSpot(security);
  const supportsStrike = isOption(security);
  const supportsType = !isSpot(security);
  const supportsExpiry = isOption(security) || isFuture(security);
  const isUnifiedLeg = legIndex == null;
  const showNoMarketsMessage = (!supportsAllMarkets || isUnifiedLeg) && !legMarketNames?.length && touched;

  // How many rows of information are visible in the non-expanded state. This is needed to determine the height.
  const showHaircutRatio = enableHaircutRatio && haircutRatio != null;
  const nonExpandedRows = (isInitiating == null ? 3 : 4) + (showHaircutRatio ? 1 : 0);

  return (
    <Section
      ref={sectionRef}
      isExpanded={isExpanded}
      nonExpandedRows={nonExpandedRows}
      mt="spacing4"
      data-testid={`ml-details-leg-${legIndex ?? 'ul'}`}
    >
      <VStack alignItems="stretch">
        <Row gap="spacing8" justifyContent="flex-start" onClick={handleToggle}>
          <HStack flex={1} style={{ cursor: 'pointer' }} justifyContent="flex-start">
            <Tooltip tooltip={isUnifiedLeg ? 'Unified Liquidity' : undefined}>
              <IndicatorBadge mr="spacing4">
                {isUnifiedLeg ? <Icon icon={IconName.Database} rotate={-90} /> : <>#{legIndex + 1}</>}
              </IndicatorBadge>
            </Tooltip>
            {ratio && (
              <Ratio data-testid="leg-ratio" value={ratio}>
                {ratio > 0 ? `+${ratio}` : `${ratio}`}
              </Ratio>
            )}
            <Text whiteSpace="nowrap" fontSize="fontSizeSmall" color="colorTextImportant">
              {prettyLegName ?? security.DisplaySymbol ?? symbol}
            </Text>
          </HStack>
          {showNoMarketsMessage && (
            <Text ml="spacing4" color="colorTextNegative" size="fontSizeSmall">
              No Markets
            </Text>
          )}
        </Row>

        <Row justifyContent="space-between" gap="spacing4">
          <Text color="colorTextDefault" size="fontSizeSmall">
            Markets
          </Text>
          <Box flex="0 1 auto" overflow="hidden">
            <MultilegMarketAccountSelectorButton
              showAllMarketsToggle={!isUnifiedLeg && supportsAllMarkets}
              selectedMarketAccounts={legMarketNames}
              marketDetails={marketDetails}
              readOnly={readOnly}
              showChanges={showLegMarketChanges}
              anchorRef={editMarketsButtonRef}
              onClick={handleOpenMarketSelector}
            />
          </Box>
        </Row>

        {isInitiating == null ? null : (
          <Row justifyContent="space-between">
            <Text color="colorTextDefault" whiteSpace="nowrap" fontSize="fontSizeSmall">
              Initiating
            </Text>
            <Checkbox
              checked={isInitiating}
              disabled={readOnly}
              size={FormControlSizes.Small}
              onChange={handleInitiatingChange}
              data-testid="ml-leg-initiating"
            />
          </Row>
        )}
        {enableHaircutRatio && haircutRatio != null ? (
          <Row justifyContent="space-between">
            <Tooltip
              showUnderline
              tooltip="Valid range: [0–1). Discounts this leg's usable liquidity for computing decisions (e.g., 0.20 = use 80% of visible depth)"
            >
              <Text color="colorTextDefault" whiteSpace="nowrap" fontSize="fontSizeSmall">
                Haircut Ratio
              </Text>
            </Tooltip>
            <NumberInput
              value={haircutRatio}
              disabled={readOnly}
              size={FormControlSizes.Small}
              onChange={handleHaircutRatioChange}
              onBlur={() => {
                if (haircutRatio === '') {
                  handleHaircutRatioChange('0');
                }
              }}
              min="0"
              width="80px"
              data-testid="ml-leg-haircut-ratio"
            />
          </Row>
        ) : null}
      </VStack>

      {isExpanded && (
        <VStack alignItems="stretch">
          {supportsAsset ? getInfoRow('Asset', security?.BaseCurrency) : null}
          {getInfoRow('Instrument', security?.ProductType)}
          {security?.ProductType !== ProductTypeEnum.Spot && getInfoRow('Exchange', exchange?.DisplayName)}
          {supportsExpiry ? getInfoRow('Expiry Date', expiration) : null}
          {supportsStrike ? getInfoRow('Strike', security?.StrikePrice) : null}
          {supportsType ? getInfoRow('Type', security?.OptionType || security?.SettleValueType || '-') : null}
          {ratio ? getInfoRow('Ratio', ratio > 0 ? `+${ratio}` : `${ratio}`) : null}
        </VStack>
      )}

      <Row
        fontSize="fontSizeSm"
        w="100%"
        gap="spacing8"
        color="colorTextSubtle"
        cursor="pointer"
        onClick={handleToggle}
      >
        <Text>More Details</Text>
        <Box alignSelf="center" flex="1">
          <Divider />
        </Box>
        <Icon icon={IconName.ChevronDown} rotate={isExpanded ? 180 : 0} size={ICON_SIZES.SMALL} />
      </Row>
    </Section>
  );
};

const Row = styled(HStack)`
  height: 24px;
`;
